import { createContext, useContext, useState, useCallback, ReactNode, FC } from 'react';

interface LoaderState {
  isLoading: boolean;
  message?: string;
}

interface LoaderContextType extends LoaderState {
  showLoader: (message?: string) => void;
  hideLoader: () => void;
  // Optional: Helper to wrap async functions automatically
  withLoader: <T>(fn: () => Promise<T>, message?: string) => Promise<T>;
}

const LoaderContext = createContext<LoaderContextType | undefined>(undefined);

interface LoaderProviderProps {
  children: ReactNode;
}

export const LoaderProvider: FC<LoaderProviderProps> = ({ children }) => {
  const [state, setState] = useState<LoaderState>({ isLoading: false });

  const showLoader = useCallback((message?: string) => {
    setState({ isLoading: true, message });
  }, []);

  const hideLoader = useCallback(() => {
    setState({ isLoading: false, message: undefined });
  }, []);

  // Helper to avoid try/finally blocks everywhere
  const withLoader = useCallback(
    async <T,>(fn: () => Promise<T>, message?: string): Promise<T> => {
      showLoader(message);
      try {
        return await fn();
      } finally {
        hideLoader();
      }
    },
    [showLoader, hideLoader]
  );

  return (
    <LoaderContext.Provider value={{ ...state, showLoader, hideLoader, withLoader }}>
      {children}
    </LoaderContext.Provider>
  );
};

export const useLoader = () => {
  const context = useContext(LoaderContext);
  if (!context) throw new Error('useLoader must be used within a LoaderProvider');
  return context;
};
